import { motion, useAnimation } from "framer-motion";
import { t } from "i18next";
import { isEmpty } from "lodash";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from "react";
import { Trans } from "react-i18next";
import styled, { useTheme } from "styled-components";
import LightColors from "../../constants/LightColors";
import { ReactComponent as ArrowRightIcon } from "../../icons/arrow.right.svg";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import { FAN_BUDDY_MBTI, KEYWORDS } from "../../utils/FanBuddyHelper";
import ProfileAvatar from "../thumbnails/ProfileAvatar";

const CardContainer = styled.div`
  width: 100%;
  max-width: 327px;
  padding: 20px 22px 26px 22px;
  display: flex;
  border-radius: 18px;
  flex-direction: column;
  background: ${(props) =>
    props.theme.isDark
      ? `
        linear-gradient(
          145deg,
          rgba(34, 34, 34, 0.8) 0%,
          rgba(34, 34, 34, 0.9) 5.61%,
          rgba(34, 34, 34, 0.3) 19.79%,
          rgba(34, 34, 34, 0.9) 36%,
          rgba(34, 34, 34, 0.5) 70%,
          rgba(34, 34, 34, 0.9) 100%
        ),
        linear-gradient(
          205deg,
          rgba(${props.backgroundPrimaryColor}, 0.7) 0%,
          rgba(34, 34, 34, 0.2) 41.2%,
          rgba(${props.backgroundSecondaryColor}, 0.7) 82.4%
        )
      `
      : `
        linear-gradient(
          158deg,
          rgba(255, 255, 255, 0.8) 0%,
          rgba(255, 255, 255, 0.9) 5.61%,
          rgba(255, 255, 255, 0.3) 19.79%,
          rgba(255, 255, 255, 0.9) 36%,
          rgba(255, 255, 255, 0.5) 70%,
          rgba(255, 255, 255, 0.9) 100%
        ),
        linear-gradient(
          205deg,
          rgba(${props.backgroundPrimaryColor}) 0%,
          rgba(255, 255, 255, 0.2) 41.2%,
          rgba(${props.backgroundSecondaryColor}) 82.4%
        )
      `};
  box-shadow: ${(props) =>
    props.theme.isDark
      ? `
        0px -6px 40px 0px rgba(0,0,0,0.2),
        0px 8px 16px rgba(0,0,0,0.1)
      `
      : `
        0px -6px 40px 0px rgba(${props.backgroundPrimaryColor}, 0.2),
        0px 8px 16px rgba(0, 0, 0, 0.05)
      `};
  backdrop-filter: blur(8px);
`;

function Nickname({ children, className = '', ...props }) {
  return (
    <div
      className={`text-[24px] font-semibold leading-[29px] text-main-light-1 dark:text-main-dark-1 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

function Description({ children, className = '', ...props }) {
  return (
    <div
      className={`text-[14px] font-normal leading-[17px] text-main-light-2 dark:text-main-dark-2 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

function MutualMembersText({ children, className = '', ...props }) {
  return (
    <span
      className={`text-[12px] font-normal leading-[20px] text-left text-main-light-4 dark:text-main-dark-4 ${className}`}
      {...props}
    >
      {children}
    </span>
  );
}

const MBTIBox = styled.div`
  border-radius: 8px;
  padding: 16px 12px;
  position: relative;
  backdrop-filter: blur(24px);
  border: ${({ theme }) => theme.isDark ? '1px solid rgba(21, 21, 21, 0.60)' : '1px solid rgba(255, 255, 255, 0.60)'};
  background: ${({ theme }) =>
    theme.isDark
      ? `
        linear-gradient(0deg, rgba(34, 34, 34, 0.50) 0%, rgba(34, 34, 34, 0.50) 100%), linear-gradient(92deg, rgba(21, 21, 21, 0.50) 31.85%, rgba(21, 21, 21, 0.08) 102.92%)
      `
      : `
        linear-gradient(0deg, rgba(255, 255, 255, 0.50) 0%, rgba(255, 255, 255, 0.50) 100%), linear-gradient(92deg, rgba(255, 255, 255, 0.50) 31.85%, rgba(255, 255, 255, 0.10) 102.92%)
      `};
  box-shadow: ${({ theme }) => theme.isDark ? '0px 2px 10px 0px rgba(0, 0, 0, 0)' : '0px 2px 10px 0px rgba(0, 0, 0, 0.05)'};
`;

// const MBTIBox = styled.div`
//   background: linear-gradient(91.59deg, rgba(255, 255, 255, 0.5) 31.85%, rgba(255, 255, 255, 0.1) 102.92%), linear-gradient(0deg, rgba(255, 255, 255, 0.49), rgba(255, 255, 255, 0.49));
//   border: 1px solid;
//   border-image-source: linear-gradient(134.41deg, rgba(255, 255, 255, 0.6) 5.69%, rgba(255, 255, 255, 0) 95.22%), linear-gradient(0deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0));
//   border-radius: 8px;
//   border-image-source: linear-gradient(134.41deg, rgba(255, 255, 255, 0.6) 5.69%, rgba(255, 255, 255, 0) 95.22%);
//   backdrop-filter: blur(40px);
//   box-shadow: 0px 0px 24px 0px rgba(255, 255, 255, 0.3) inset;
//   box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.04);
// `;

const MBTITitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  background: ${(props) =>
    `linear-gradient(90deg, rgba(${props.textPrimaryColor},1) 0%, rgba(${props.textSecondaryColor},1) 100%)`};
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
`;

const IconButton = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  svg {
    width: 24px;
    height: 24px;
    color: ${LightColors.textmainlv3};
  }
`;

const SquareButton = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  padding: 2px;
  word-break: keep-all;
  color: ${({ textColor }) => textColor ?? LightColors.textmainlv4};
  ${({ variant, isMine }) =>
    !isMine && variant === "mutual" &&
    `
      color: transparent;
      background-clip: text;
      -webkit-background-clip: text;
      background-image: conic-gradient(from 135deg at 50% 50%, #FFDB57 0deg, #DE69FF 102.6deg, #38B6FF 232.2deg, #FFDB57 360deg), radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0.20) 0%, rgba(255, 255, 255, 0.00) 100%);
    `}
`;

const FanBuddySwipeCard = forwardRef(
  (
    {
      isMine = false,
      currentUserKeywords,
      buddyUserProfile,
      onSelectedMutualMembers,
      onSelectedMatching,
      onSelectedBuddyUserProfile
    },
    ref
  ) => {
    const controls = useAnimation();
    const theme = useTheme();

    const isCurrentUserKeyword = useCallback(
      (keyword) => {
        return currentUserKeywords?.some((k) => k.name === keyword) || false;
      },
      [currentUserKeywords]
    );

    const isBuddyKeyword = useCallback(
      (keyword) => {
        return buddyUserProfile.keywords.some((k) => k.name === keyword);
      },
      [buddyUserProfile.keywords]
    );

    const getMutualKeywords = useMemo(() => {
      return KEYWORDS.filter(
        (keyword) => isCurrentUserKeyword(keyword.value) && isBuddyKeyword(keyword.value)
      ).map((keyword) => keyword.value);
    }, [isCurrentUserKeyword, isBuddyKeyword]);

    useImperativeHandle(ref, () => ({
      triggerSwipeAnimation: async (direction) => {
        await controls.start({
          x: direction === 'right' ? 500 : -500,
          opacity: 0,
          rotate: direction === 'right' ? 50 : -50,
          transition: { duration: 0.3 }
        });
        controls.set({ x: 0, opacity: 1, rotate: 0 });
      }
    }));

    const keywordVariant = (keyword) => {
      if (isCurrentUserKeyword(keyword.value) && isBuddyKeyword(keyword.value)) {
        return 'mutual';
      }
      if (isBuddyKeyword(keyword.value)) {
        return 'buddy';
      }
      return 'neutral';
    };

    const keywordVariantBoxShadow = (keyword) => {
      if (isCurrentUserKeyword(keyword.value) && isBuddyKeyword(keyword.value)) {
        return 'inset 0px 0px 1px 2px rgba(0, 0, 0, 0.02)';
      }
      if (isBuddyKeyword(keyword.value)) {
        return '0px 0px 1px 2px rgba(0, 0, 0, 0.02)';
      }
      return '0px 0px 1px 2px rgba(0, 0, 0, 0.02)';
    };

    // const keywordVariantBackground = (keyword) => {
    //   if (isCurrentUserKeyword(keyword.value) && isBuddyKeyword(keyword.value)) {
    //     return 'radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%),linear-gradient(0deg, rgba(255, 255, 255, 0.68), rgba(255, 255, 255, 0.68)), conic-gradient(from 135deg at 50% 50%, #FFEFB5 0deg, #EAB0FA 102.6deg, #90ECFF 232.2deg, #FFEFB5 360deg)';
    //   }
    //   if (isBuddyKeyword(keyword.value)) {
    //     return 'rgba(255, 255, 255, 0.8)';
    //   }
    //   return 'linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.25) 100%)';
    // };
    const keywordVariantBackground = (keyword) => {
      const isMyKeyword = isCurrentUserKeyword(keyword.value);
      const isBuddy = isBuddyKeyword(keyword.value);

      if (isMine) {
        if (isMyKeyword) { // 좋아요: 흰색(라이트), pure black(다크)
          return theme.isDark ? "#222222" : "#FFFFFF";
        } else { // neutral: 흰색(라이트), 회색(다크)
          return theme.isDark
          ? "linear-gradient(180deg, rgba(34,34,34,0.5) 0%, rgba(34,34,34,0.25) 100%)"
          : "linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.25) 100%)";
        }
      } else {
        if (isMyKeyword && isBuddy) { // 무지개 그라데이션
          return theme.isDark ?
            `
              linear-gradient(0deg, rgba(34,34,34,0.600), rgba(34,34,34,0.50)),
              conic-gradient(from 135deg at 50% 50%, #FFEFB5 0deg, #EAB0FA 102.6deg, #90ECFF 232.2deg, #FFEFB5 360deg),
              radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255,255,255,0) 100%)
            ` :
            "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255,255,255,0) 100%), linear-gradient(0deg, rgba(255,255,255,0.68), rgba(255,255,255,0.68)), conic-gradient(from 135deg at 50% 50%, #FFEFB5 0deg, #EAB0FA 102.6deg, #90ECFF 232.2deg, #FFEFB5 360deg)";
        } else if (isBuddy) { // 좋아요: 흰색(라이트), pure black(다크)
          return theme.isDark ? "#222222" : "#FFFFFF";
        } else {
          return theme.isDark
            ? "linear-gradient(180deg, rgba(34,34,34,0.5) 0%, rgba(34,34,34,0.25) 100%)"
            : "linear-gradient(180deg, rgba(255,255,255,0.15) 0%,  rgba(255,255,255,0.55) 100%)";
        }
      }

      // if (isMyKeyword && isBuddy) {
      //   if (isMine) {
      //     return theme.isDark ? "#222222" : "#FFFFFF";
      //   }
      //   if (theme.isDark) {
      //     // 다크모드 mutual: 어두운 레이어를 맨 위에!
      //      return `
      //       linear-gradient(0deg, rgba(34,34,34,0.600), rgba(34,34,34,0.50)),
      //       conic-gradient(from 135deg at 50% 50%, #FFEFB5 0deg, #EAB0FA 102.6deg, #90ECFF 232.2deg, #FFEFB5 360deg),
      //       radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255,255,255,0) 100%)
      //     `;
      //   }
      //   // 무지개 그라데이션
      //   return "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255,255,255,0) 100%), linear-gradient(0deg, rgba(255,255,255,0.68), rgba(255,255,255,0.68)), conic-gradient(from 135deg at 50% 50%, #FFEFB5 0deg, #EAB0FA 102.6deg, #90ECFF 232.2deg, #FFEFB5 360deg)";
      // }
      // if (isMyKeyword && !isBuddy) {
      //   // 나만 좋아요: 흰색(라이트), pure black(다크)
      //   return theme.isDark ? "#222222" : "#FFFFFF";
      // }
      // if (!isMyKeyword && isBuddy) {
      //   // 상대방만 좋아요: 흰색+투명(라이트), 검정+투명(다크)
      //   return theme.isDark
      //     ? "linear-gradient(180deg, #222222 0%, rgba(34,34,34,0.5) 100%)"
      //     : "linear-gradient(180deg, #FFFFFF 0%, rgba(255,255,255,0.5) 100%)";
      // }
      // // neutral (아무도 좋아요 안함 다크모드는 완료)
      // return theme.isDark
      //   ? "linear-gradient(180deg, rgba(34,34,34,0.5) 0%, rgba(34,34,34,0.25) 100%)"
      //   : "linear-gradient(180deg, rgba(255,255,255,0.15) 0%,  rgba(255,255,255,0.55) 100%)";
    };

    const keywordVariantTextColor = (keyword) => {
      const isMyKeyword = isCurrentUserKeyword(keyword.value);
      const isBuddy = isBuddyKeyword(keyword.value);
      if (isMine) {
        if (isMyKeyword) {
          return theme.isDark ? "#B0B0B0" : LightColors.textmainlv4;
        }
        return theme.isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)";
      } else {
        if (isMyKeyword && isBuddy) {
          return 'transparent';
        }
        if (isBuddy) {
          return theme.isDark ? "#B0B0B0" : LightColors.textmainlv4;
        }
        return theme.isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)";
      }

      // if (isMyKeyword && isBuddy) {
      //   if(isMine) {
      //     return theme.isDark ? "#B0B0B0" : LightColors.textmainlv4;
      //   }
      //   // mutual: 무지개 텍스트(아래 styled에서 처리)
      //   return 'transparent';
      // }
      // if (isMyKeyword && !isBuddy) {
      //   // 나만 좋아요
      //   return theme.isDark ? "#B0B0B0" : LightColors.textmainlv4;
      // }
      // // 나머지(상대만/아무도)
      // return theme.isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)";
    };

    return (
      <motion.div animate={controls} style={{ cursor: 'default' }}>
        <CardContainer
          initial={{ scale: 0.75, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.3 }}
          backgroundPrimaryColor={FAN_BUDDY_MBTI[buddyUserProfile.mbti].backgroundColor.primary}
          backgroundSecondaryColor={FAN_BUDDY_MBTI[buddyUserProfile.mbti].backgroundColor.secondary}
        >
          {/* Mutual Members Section */}
          {(
            <div
              className="w-full flex justify-between place-items-center mb-[18px]"
              onClick={() =>
                !isEmpty(buddyUserProfile.mutualMembers) &&
                onSelectedMutualMembers(buddyUserProfile)
              }
            >
              <div className="flex flex-row gap-[2px] place-items-center">
                {!isEmpty(buddyUserProfile.mutualMembers) && (
                  <img
                    src="/assets/icons/like.shape.shine.png"
                    alt="mutual"
                    width={20}
                    height={20}
                  />
                )}
                <MutualMembersText>
                  {isEmpty(buddyUserProfile.mutualMembers) ? (
                    <>{t('FanBuddySwipeCard.emptyMutualArtist')}</>
                  ) : buddyUserProfile.mutualMembers.length <= 2 ? (
                    <Trans
                      i18nKey="UserProfileSearchPage.mutualBias"
                      components={{ bold: <strong /> }}
                      values={{
                        members: buddyUserProfile?.mutualMembers
                          ?.map((member) => member.name)
                          ?.join(', ')
                      }}
                    />
                  ) : (
                    <Trans
                      i18nKey="UserProfileSearchPage.mutualBiasMore"
                      components={{ bold: <strong /> }}
                      values={{
                        members: `${buddyUserProfile?.mutualMembers
                          ?.map((member) => member.name)
                          ?.slice(0, 2)
                          .join(', ')}`,
                        count: buddyUserProfile?.mutualMembers?.length - 2
                      }}
                    />
                  )}
                </MutualMembersText>
              </div>
              {!isEmpty(buddyUserProfile.mutualMembers) && (
                <ArrowRightIcon width={'14px'} height={'14px'} className="fill-onDisable-light dark:fill-onDisable-dark" />
              )}
            </div>
          )}

          {/* Profile Section */}
          <div
            className={`flex justify-between w-full ${
              isEmpty(buddyUserProfile.mutualMembers) ? '' : 'pt-4'
            }`}
            onClick={() => onSelectedBuddyUserProfile(buddyUserProfile)}
          >
            <div className="flex flex-col self-center flex-1 min-w-0 mr-[12px] gap-[8px]">
              <div className="flex flex-row items-center min-w-0 gap-[5px]">
                <div className="min-w-0">
                  <Nickname className="truncate">{buddyUserProfile.nickname}</Nickname>
                </div>
                {
                  !buddyUserProfile.isTheSameAge &&
                  <div className="text-[11px] flex-shrink-0 px-[7px] py-[4px] rounded-[30px] bg-surface-light dark:bg-surface-dark text-main-light-4 dark:text-main-dark-4">
                    {t('FanBuddySwipeCard.theSameAge')}
                  </div>
                }
              </div>
              {!isEmpty(buddyUserProfile.description) && (
                <Description className="truncate">{buddyUserProfile.description}</Description>
              )}
            </div>
            <ProfileAvatar
              profileImageUrl={buddyUserProfile.thumbnailUrl}
              widthAndHeight="60px"
              svgWidthAndHeight="48px"
              className="flex-shrink-0"
            />
          </div>
          {/* MBTI Description */}
          <MBTIBox
            className="flex flex-col px-4 py-3 text-center mt-5 gap-1.5 relative"
            onClick={() => onSelectedMatching(buddyUserProfile)}
          >
            <IconButton className="rounded-full absolute top-2 right-2 p-[2px] bg-surface-light dark:bg-surface-dark">
              <SearchIcon width={'12px'} height={'12px'} className="fill-main-light-4 dark:fill-main-dark-4" />
            </IconButton>
            <MBTITitle
              textPrimaryColor={FAN_BUDDY_MBTI[buddyUserProfile.mbti]?.textColor?.primary}
              textSecondaryColor={FAN_BUDDY_MBTI[buddyUserProfile.mbti]?.textColor?.secondary}
            >
              {FAN_BUDDY_MBTI[buddyUserProfile.mbti]?.name}
            </MBTITitle>
            <div className="text-[12px] font-normal leading-[14px] text-center text-main-light-3 dark:text-main-dark-3">
              {FAN_BUDDY_MBTI[buddyUserProfile.mbti]?.recap}
            </div>
          </MBTIBox>
          {
            <div className="flex justify-between w-full mt-[24px]">
              {
                isMine ? (
                  <div className="text-[13px] font-normal leading-[16px] text-main-light-3 dark:text-main-dark-3">
                    {t('FanBuddySwipeCard.myKeywordsTitle')}
                  </div>
                ) : (isEmpty(getMutualKeywords)) ? (
                  <div className="text-[13px] font-normal leading-[16px] text-main-light-3 dark:text-main-dark-3">
                    {t('FanBuddySwipeCard.emptyKeywordsTitle')}
                  </div>
                ) : (
                  <div className="flex flex-row gap-[2px] place-items-center">
                    <div className="text-[12px] font-normal leading-[16px] text-main-light-4 dark:text-main-dark-4">
                      <Trans
                        i18nKey="FanBuddySwipeCard.mutualKeywordsCount"
                        components={{ bold: <strong /> }}
                        values={{ count: getMutualKeywords.length }}
                      />
                    </div>
                  </div>
                )
              }
            </div>
          }
          {/* Keywords Section */}
          {
            <div className="grid grid-cols-4 gap-[5px] w-full text-center mt-[12px]">
              {KEYWORDS.map((keyword) => (
                <motion.div
                  key={keyword.key}
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 15
                  }}
                  // style={{
                  //   justifyItems: 'center',
                  //   alignContent: 'center',
                  //   boxShadow: keywordVariantBoxShadow(keyword),
                  //   background: keywordVariantBackground(keyword),
                  //   borderRadius: '12px',
                  //   padding: '2px',
                  //   width: '100%',
                  //   aspectRatio: '1/1'
                  // }}
                  style={{
                    justifyItems: "center",
                    alignContent: "center",
                    boxShadow:
                      !isMine && keywordVariant(keyword) === "mutual"
                        ? !theme.isDark
                          ? `
                            0 -2px 4px 0px rgba(144,236,255,0.32),    /* 위쪽(파랑, 하늘색) */
                            2px 2px 4px 0px rgba(151,255,198,0.22),   /* 오른쪽 아래(연두) */
                            0 2px 4px 0px rgba(255,239,181,0.22),     /* 아래쪽(노랑) */
                            -2px -2px 4px 0px rgba(234,176,250,0.22)  /* 왼쪽 위(보라) */
                            `
                          : `
                            0 0 7px 0px rgba(255,255,255,0.68),
                            0 0 14px 0px rgba(255,255,255,0.18)
                            `
                        : keywordVariantBoxShadow(keyword),
                    background: keywordVariantBackground(keyword),
                    borderRadius: "12px",
                    padding: "2px",
                    width: "100%",
                    aspectRatio: "1/1",
                    ...(!isMine && keywordVariant(keyword) === "mutual"
                      ? { backdropFilter: "blur(7px)" }
                      : {}),
                  }}
                >
                  {/* {
                  isCurrentUserKeyword(keyword.value) && isBuddyKeyword(keyword.value) ?
                  <OctagonButton
                    key={keyword.key}
                    text={keyword.value}
                    isSelected={true}
                  /> : (
                    isBuddyKeyword(keyword.value) ?
                    <HexagonButton
                      key={keyword.key}
                      text={keyword.value}
                    /> :
                    <PentagonButton
                      key={keyword.key}
                      text={keyword.value}
                    />
                  )
                } */}
                  <SquareButton
                    variant={keywordVariant(keyword)}
                    textColor={keywordVariantTextColor(keyword)}
                    isMine={isMine}
                  >
                    {keyword.value}
                  </SquareButton>
                </motion.div>
              ))}
            </div>
          }
        </CardContainer>
      </motion.div>
    );
  }
);

export default FanBuddySwipeCard;
